/**
 * ReferenceEditModal - Edit reference document metadata
 * 
 * Allows updating title, standardNumber, description, and other metadata
 * after a document has been uploaded. Triggers reindex if standardNumber changes.
 */

import React, { useState, useEffect } from 'react';
import { referenceDocumentsApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Label,
  Spinner,
} from '@/components/ui';
import type { ReferenceDocumentCard } from '../../types/panel-types';
import { logger } from '../../utils/logger';

interface ReferenceEditModalProps {
  document: ReferenceDocumentCard;
  onClose: () => void;
  onSuccess: () => void;
  open?: boolean;
}

export default function ReferenceEditModal({ 
  document, 
  onClose, 
  onSuccess,
  open = true 
}: ReferenceEditModalProps) {
  const [saving, setSaving] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state - initialize from document
  const [title, setTitle] = useState(document.title || '');
  const [standardNumber, setStandardNumber] = useState(document.standardNumber || '');
  const [description, setDescription] = useState(document.description || '');
  const [revisionDate, setRevisionDate] = useState(document.revisionDate || '');
  const [tags, setTags] = useState((document.tags || []).join(', '));

  // Track original standardNumber to detect changes
  const originalStandardNumber = document.standardNumber || '';

  // Reset form when document changes
  useEffect(() => {
    setTitle(document.title || '');
    setStandardNumber(document.standardNumber || '');
    setDescription(document.description || '');
    setRevisionDate(document.revisionDate || '');
    setTags((document.tags || []).join(', '));
    setError(null);
    setSuccess(null);
  }, [document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!standardNumber.trim()) {
      setError('Standard Number is required (e.g., DO-254, ARP4761)');
      return;
    }

    try {
      setError(null);
      setSaving(true);

      const updates = {
        title: title.trim(),
        standardNumber: standardNumber.trim(),
        description: description.trim(),
        revisionDate: revisionDate.trim(),
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      await referenceDocumentsApi.update(document.documentId, updates);

      // If standardNumber changed, trigger reindex to update Neptune graph
      const standardNumberChanged = standardNumber.trim() !== originalStandardNumber;
      if (standardNumberChanged) {
        setSuccess('Metadata updated. Reindexing to update graph...');
        setReindexing(true);
        
        try {
          await referenceDocumentsApi.reindex(document.documentId);
          setSuccess('Metadata updated and reindex started. The document will be searchable by the new standard number shortly.');
        } catch (reindexError) {
          logger.error('Reindex failed:', reindexError);
          setSuccess('Metadata updated but reindex failed. Please manually reindex the document.');
        } finally {
          setReindexing(false);
        }
      } else {
        setSuccess('Metadata updated successfully.');
      }

      // Auto-close after success
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);

    } catch (err) {
      logger.error('Failed to update document:', err);
      setError(err instanceof Error ? err.message : 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const isProcessing = saving || reindexing;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Reference Document</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Standard Number - Required */}
          <div className="space-y-2">
            <Label htmlFor="standardNumber" className="flex items-center gap-1">
              Standard Number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="standardNumber"
              placeholder="e.g., DO-254, ARP4761, AC 20-152"
              value={standardNumber}
              onChange={(e) => setStandardNumber(e.target.value)}
              disabled={isProcessing}
              className={!standardNumber.trim() ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              This is how the AI will find and reference this document. Must match common usage.
            </p>
            {standardNumber.trim() !== originalStandardNumber && originalStandardNumber && (
              <p className="text-xs text-warning">
                ⚠️ Changing the standard number will trigger a reindex to update the graph database.
              </p>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="flex items-center gap-1">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Document title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of this document"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isProcessing}
            />
          </div>

          {/* Revision Date */}
          <div className="space-y-2">
            <Label htmlFor="revisionDate">Revision Date</Label>
            <Input
              id="revisionDate"
              type="date"
              value={revisionDate}
              onChange={(e) => setRevisionDate(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              placeholder="hardware, safety, certification (comma-separated)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          {/* Status Messages */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {error}
            </div>
          )}
          
          {success && (
            <div className="text-sm text-success bg-success/10 p-3 rounded-md flex items-center gap-2">
              {reindexing && <Spinner className="w-4 h-4" />}
              {success}
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isProcessing || !title.trim() || !standardNumber.trim()}
            >
              {saving ? 'Saving...' : reindexing ? 'Reindexing...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
